import { useI18n } from '@ecom/i18n';
import { useId, type ReactElement, type ReactNode } from 'react';

export interface FieldProps {
  readonly label: string;
  readonly optional?: boolean;
  readonly error?: string;
  readonly hint?: string;
  /** Receives `{ id, 'aria-describedby', 'aria-invalid' }` to spread onto the actual control. */
  readonly children: (bind: FieldBindProps) => ReactElement;
}

export interface FieldBindProps {
  readonly id: string;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-invalid': true | undefined;
}

/**
 * Label + control + error/hint, wired for accessibility (DEV §13, WCAG).
 *
 * The render-prop shape (`children` is a function, not the control itself)
 * exists so `Field` can generate one stable id and hand it — plus the
 * matching `aria-describedby` — to whichever control is inside, without that
 * control needing to know Field exists. `Input`, `Select`, `PhoneInput` and
 * `GovernorateSelect` all accept exactly this bind shape.
 */
export function Field({ label, optional = false, error, hint, children }: FieldProps): ReactNode {
  const { t } = useI18n();
  const id = useId();
  const errorId = error !== undefined ? `${id}-error` : undefined;
  const hintId = hint !== undefined ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="ecom-field">
      <div className="ecom-field__label-row">
        <label htmlFor={id} className="ecom-field__label">
          {label}
        </label>
        {optional ? <span className="ecom-field__optional">{t('common.optional')}</span> : null}
      </div>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error !== undefined ? true : undefined,
      })}

      {error !== undefined ? (
        <p id={errorId} className="ecom-field__error" role="alert">
          {error}
        </p>
      ) : null}
      {error === undefined && hint !== undefined ? (
        <p id={hintId} className="ecom-field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
